/**
 * Where "Buka dokumen asli" should LAND inside the original file — decided from what
 * the citation's location fields have actually been proven to mean, not from hope.
 *
 * The identity of each field, established by reading the ingestion code:
 *
 * - `page_number` is a 1-based page of the ORIGINAL file. It is only ever set from
 *   physical page enumeration: the table-splice path numbers pypdfium2 pages with
 *   `enumerate(page_texts, start=1)` (`src/aceh_rag/services/parser.py:346`) and the
 *   OCR path renders original pages by `[number - 1]` pdfium index
 *   (`src/aceh_rag/services/ingestion.py:236`). When it is non-null it names a real
 *   page; born-digital non-table PDFs simply do not carry one.
 *
 * - `char_start`/`char_end` are code-point offsets into `ParsedDocument.text` — the
 *   EXTRACTED text (`src/aceh_rag/chunkers/chunking.py:150,166` and the
 *   normalized-whitespace fallback mapping back into that same string). For every
 *   format that goes through markdown extraction (PDF, DOCX, HTML, CSV, XLSX) that
 *   string does not correspond to the original bytes, so the offsets MUST NOT be
 *   applied to the fetched original. The one proven case is `.txt`: the parser sets
 *   `text = content.decode("utf-8")` and then strips it
 *   (`src/aceh_rag/services/parser.py:126,681`), so the offsets index the decoded,
 *   stripped original — exactly what the reader fetches, after the same strip.
 *
 * Anything outside those two proven cases is `unlocated`, and the reader says so
 * out loud rather than opening at the top pretending that was the target.
 */

export interface CitationLocation {
  page_number: number | null;
  char_start: number | null;
  char_end: number | null;
}

export type OriginalLanding =
  | { kind: "pdf-page"; page: number }
  | { kind: "text-range"; start: number; end: number }
  /** No proven location inside the original — the honest fallback, not an error. */
  | { kind: "unlocated" };

export function originalLanding(
  citation: CitationLocation,
  mediaType: string,
): OriginalLanding {
  if (mediaType.includes("pdf")) {
    const page = citation.page_number;
    if (typeof page === "number" && Number.isInteger(page) && page >= 1) {
      return { kind: "pdf-page", page };
    }
    return { kind: "unlocated" };
  }

  // ONLY text/plain: for every other text-ish format (HTML, CSV, JSON) the indexed
  // text came out of an extraction step and the offsets do not index the raw file.
  if (mediaType.split(";")[0]?.trim() === "text/plain") {
    const { char_start: start, char_end: end } = citation;
    if (
      typeof start === "number" &&
      typeof end === "number" &&
      Number.isInteger(start) &&
      Number.isInteger(end) &&
      start >= 0 &&
      end > start
    ) {
      return { kind: "text-range", start, end };
    }
  }

  return { kind: "unlocated" };
}

/** The PDF open-parameter fragment — honoured by Chromium's PDFium viewer and
 *  Firefox's pdf.js for embedded object/iframe URLs, blob: URLs included. */
export function pdfUrlAtPage(url: string, page: number): string {
  return `${url}#page=${page}`;
}

export interface HighlightedText {
  before: string;
  cited: string;
  after: string;
}

/**
 * Split the fetched original text around the cited range, or refuse.
 *
 * The offsets index the parser's `content.decode("utf-8").strip()`, so the same
 * strip is applied here before slicing. A range that falls outside the stripped
 * body, or lands on nothing but whitespace, is refused (`null`) — the caller then
 * shows the explicit "location unavailable" fallback instead of a wrong highlight.
 */
export function splitTextRange(
  body: string,
  start: number,
  end: number,
): HighlightedText | null {
  const stripped = body.trim();
  if (!stripped) return null;
  if (start < 0 || end <= start || end > stripped.length) return null;
  const cited = stripped.slice(start, end);
  if (!cited.trim()) return null;
  return {
    before: stripped.slice(0, start),
    cited,
    after: stripped.slice(end),
  };
}
